import { z } from 'zod';

/**
 * A migrated member's Pelago 1.0 weekly plans, as context for weekly-plan
 * theme generation.
 *
 * In 1.0 a weekly plan's content is a `user_intentions` row: the weekly-plan
 * save (backend2 `weekly-plans/src/service/intentions.ts`) creates it from the
 * plan's focus text with a placeholder duration, and the weekly review writes
 * how closely the member stuck to it onto the row's `status`. Galapagos mirrors
 * those rows as `legacy_user_intentions`; this module owns the shape the
 * weekly-plan generator reads them as. They are context only — never a
 * Galapagos plan.
 */

/**
 * The placeholder duration 1.0 gives every intention its weekly-plan flow
 * creates (backend2 `WEEKLY_PLAN_INTENTION_DURATION`). Standalone 1.0
 * intentions carry a real N-day duration, so this value is what marks an
 * intention as a weekly plan.
 */
export const LEGACY_WEEKLY_PLAN_INTENTION_DURATION_DAYS = 365;

/** At most this many earlier-app plans are shown to the model, newest first. */
export const LEGACY_WEEKLY_PLAN_SEED_LIMIT = 3;

/** Cap on the member's own words per plan; longer text is cut on a word boundary. */
export const LEGACY_WEEKLY_PLAN_OWN_WORDS_MAX_CHARS = 500;

/**
 * The 1.0 set-focus screen's preset labels, verbatim from the 1.0 app's
 * `strings_care_update.xml` (English) and `values-es/strings_care_update.xml`
 * (Spanish). The app stored the member's ticked labels in the app language,
 * joined with ", ", followed by any text the member wrote themselves. Kept
 * verbatim, including the Spanish trailing full stops, because that is how the
 * stored text reads.
 */
export const LEGACY_WEEKLY_PLAN_FOCUS_PRESETS = {
  en: [
    'Be kind to myself',
    'Be more active',
    'Find joy',
    'Sleep better',
    'Meditate',
    'Slow down',
    'Eat healthy',
    'Journal',
    'Stick to my goals',
    'Time with family',
    'Time with friends',
    'Stay present',
    'Self-care',
    'Make time for me',
    'Gratitude',
    'Try a new hobby',
  ],
  es: [
    'Sé amable conmigo mismo',
    'Sé más activo',
    'Come sano',
    'Encuentra alegría',
    'Gratitud',
    'Diario',
    'Haz tiempo para mí',
    'Meditar',
    'Autocuidado',
    'Duerme mejor.',
    'Más despacio.',
    'Mantente presente',
    'Cumplir mis objetivos',
    'Tiempo con la familia',
    'Tiempo con amigos',
    'Prueba un nuevo hobby',
  ],
} as const;

/**
 * The most picked areas carried per plan: one locale's full preset list. A text
 * naming more (only possible by mixing locales) keeps the first ones rather
 * than failing the parse.
 */
export const LEGACY_WEEKLY_PLAN_PICKED_FOCUSES_MAX = LEGACY_WEEKLY_PLAN_FOCUS_PRESETS.en.length;

/** 1.0's own `user_intentions.status` vocabulary: the legacy values plus the weekly-review answers. */
export const legacyIntentionStatusSchema = z.enum([
  'completed',
  'partial',
  'notCompleted',
  'notReally',
  'somewhat',
  'veryClosely',
]);

export type LegacyIntentionStatus = z.infer<typeof legacyIntentionStatusSchema>;

/** How the member followed an earlier plan, in the vocabulary the prompt reads. */
export const LEGACY_FOLLOWED_HOW = [
  'very_closely',
  'somewhat',
  'not_really',
  'completed',
  'partly',
  'not_completed',
  'not_reviewed',
] as const;

export type LegacyFollowedHow = (typeof LEGACY_FOLLOWED_HOW)[number];

const FOLLOWED_HOW_BY_STATUS: Record<LegacyIntentionStatus, LegacyFollowedHow> = {
  veryClosely: 'very_closely',
  somewhat: 'somewhat',
  notReally: 'not_really',
  completed: 'completed',
  partial: 'partly',
  notCompleted: 'not_completed',
};

/** Map a stored 1.0 status to the prompt vocabulary; anything unparseable is unreviewed. */
export function legacyFollowedHow(status: unknown): LegacyFollowedHow {
  const parsed = legacyIntentionStatusSchema.safeParse(status);
  return parsed.success ? FOLLOWED_HOW_BY_STATUS[parsed.data] : 'not_reviewed';
}

/** One earlier-app weekly plan as the weekly-plan generator sees it. */
export const weeklyPlanLegacyPlanSchema = z
  .object({
    setOn: z.string().date(),
    daysAgo: z.number().int().nonnegative(),
    followedHow: z.enum(LEGACY_FOLLOWED_HOW),
    pickedFocuses: z.array(z.string().trim().min(1)).max(LEGACY_WEEKLY_PLAN_PICKED_FOCUSES_MAX),
    ownWords: z.string().trim().min(1).max(LEGACY_WEEKLY_PLAN_OWN_WORDS_MAX_CHARS).nullable(),
  })
  .strict()
  .refine((plan) => plan.pickedFocuses.length > 0 || plan.ownWords !== null, {
    message: 'an earlier plan needs a picked focus or the member’s own words',
  });

export type WeeklyPlanLegacyPlan = z.infer<typeof weeklyPlanLegacyPlanSchema>;

/**
 * Shorten `text` to at most `max` characters, cutting at the last word boundary
 * and ending in an ellipsis. Text already within the cap is returned unchanged.
 */
export function truncateOnWordBoundary(text: string, max: number): string {
  if (text.length <= max) return text;
  const room = text.slice(0, max - 1);
  const lastSpace = room.lastIndexOf(' ');
  const cut = lastSpace > 0 ? room.slice(0, lastSpace) : room;
  return `${cut.trimEnd()}…`;
}

const normalisePreset = (label: string) => label.trim().toLowerCase().replace(/\.$/, '');

const PRESET_KEYS = new Set(
  [...LEGACY_WEEKLY_PLAN_FOCUS_PRESETS.en, ...LEGACY_WEEKLY_PLAN_FOCUS_PRESETS.es].map(
    normalisePreset,
  ),
);

const FOCUS_SEPARATOR = ', ';

/**
 * Separate a stored 1.0 focus into the preset labels the member ticked and the
 * words they wrote themselves. The app wrote the ticked labels first and the
 * member's own text last, so labels are taken from the front until the first
 * segment that is not one; that segment and everything after it are the
 * member's own words, which may contain commas of their own.
 */
export function splitLegacyFocus(text: string): {
  pickedFocuses: string[];
  ownWords: string | null;
} {
  const segments = text.trim().split(FOCUS_SEPARATOR);
  const pickedFocuses: string[] = [];
  const seen = new Set<string>();
  let index = 0;
  for (; index < segments.length; index += 1) {
    const segment = segments[index]?.trim() ?? '';
    // An empty segment is a stray separator, not the start of the member's words.
    if (segment.length === 0) continue;
    const key = normalisePreset(segment);
    if (!PRESET_KEYS.has(key)) break;
    if (!seen.has(key) && pickedFocuses.length < LEGACY_WEEKLY_PLAN_PICKED_FOCUSES_MAX) {
      seen.add(key);
      pickedFocuses.push(segment);
    }
  }
  const rest = segments
    .slice(index)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .join(FOCUS_SEPARATOR);
  return {
    pickedFocuses,
    ownWords:
      rest.length > 0 ? truncateOnWordBoundary(rest, LEGACY_WEEKLY_PLAN_OWN_WORDS_MAX_CHARS) : null,
  };
}
